import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { getCustomerStatus } from '@/lib/api';
import { CheckCircle2, Clock, Shield, FileText, MessageSquare, Phone } from 'lucide-react';
import Logo from '@/components/Logo';

export default function CustomerStatus() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await getCustomerStatus(token);
        setData(resp.data);
      } catch (err) {
        setError('Quote not found or invalid link.');
      }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full rounded-2xl border-2">
        <CardContent className="p-8 space-y-4">
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full rounded-2xl border-2">
        <CardContent className="p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="font-heading text-xl font-semibold mb-2 text-[var(--brand-slate-900)]">{error}</h2>
          <Link to="/" className="text-[var(--brand-ocean-600)] underline text-sm hover:text-[var(--brand-ocean-700)] transition-colors">
            Back to home
          </Link>
        </CardContent>
      </Card>
    </div>
  );

  const lead = data.lead;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] py-8 sm:py-12 px-4">
      <div className="mx-auto max-w-3xl">
        
        {/* BRANDING */}
        <div className="flex justify-center mb-6" data-testid="status-page-logo">
          <div className="w-32 sm:w-40">
            <Logo variant="light" size="medium" />
          </div>
        </div>

        {/* SECTION 1 – CONFIRMATION */}
        <div className="text-center mb-8" data-testid="confirmation-section">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-50 mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--brand-slate-900)] mb-3" data-testid="confirmation-headline">
            You're All Set — We've Received Your Request
          </h1>
          <p className="text-base sm:text-lg text-[var(--brand-slate-600)] max-w-xl mx-auto" data-testid="confirmation-subtext">
            We're currently reviewing your photos and preparing your quote.
          </p>
        </div>

        {/* SECTION 2 – TRUST REINFORCEMENT */}
        <Card className="rounded-2xl border-2 mb-6 bg-gradient-to-br from-[var(--brand-ocean-100)]/30 via-white to-[var(--brand-seafoam-200)]/20" data-testid="trust-reinforcement-section">
          <CardContent className="p-6 sm:p-8 text-center">
            <Shield className="h-10 w-10 text-[var(--brand-ocean-700)] mx-auto mb-3" />
            <p className="text-base sm:text-lg font-medium text-[var(--brand-slate-900)] leading-relaxed" data-testid="trust-statement">
              You just took the first step to protecting your home — without the typical sales pressure.
            </p>
          </CardContent>
        </Card>

        {/* SECTION 3 – WHAT HAPPENS NEXT */}
        <Card className="rounded-2xl border-2 mb-6" data-testid="what-happens-next-section">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading text-xl sm:text-2xl font-semibold text-[var(--brand-slate-900)] mb-6 text-center" data-testid="what-happens-next-headline">
              What Happens Next
            </h2>
            
            <div className="space-y-5 mb-6">
              <div className="flex gap-4" data-testid="step-1-review-photos">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)] flex items-center justify-center font-heading font-bold">
                  1
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm sm:text-base text-[var(--brand-slate-900)] font-medium">
                    We review your photos and project details
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-testid="step-2-build-options">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)] flex items-center justify-center font-heading font-bold">
                  2
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm sm:text-base text-[var(--brand-slate-900)] font-medium">
                    We build Good / Better / Premium options
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-testid="step-3-receive-quote">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[var(--brand-ocean-100)] text-[var(--brand-ocean-700)] flex items-center justify-center font-heading font-bold">
                  3
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm sm:text-base text-[var(--brand-slate-900)] font-medium">
                    You receive your quote within 24–48 hours
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[hsl(var(--secondary))] rounded-xl p-4 text-center" data-testid="no-appointments-statement">
              <p className="text-sm font-medium text-[var(--brand-slate-900)]">
                No appointments. No long presentations. Just clear pricing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 4 – EXPECTATIONS */}
        <Card className="rounded-2xl border-2 mb-6" data-testid="expectations-section">
          <CardContent className="p-6 sm:p-8">
            <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)] mb-4" data-testid="expectations-headline">
              What to Expect
            </h3>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm sm:text-base text-[var(--brand-slate-600)]">
                  No in-home appointment required
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm sm:text-base text-[var(--brand-slate-600)]">
                  Fast, transparent pricing
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm sm:text-base text-[var(--brand-slate-600)]">
                  No pressure
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-testid="clarification-note">
              <p className="text-xs sm:text-sm text-[var(--brand-slate-600)] leading-relaxed">
                In some cases, we may reach out if we need a quick clarification to ensure your quote is accurate — no sales calls.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* SECTION 5 – SOCIAL PROOF */}
        <Card className="rounded-2xl border-2 mb-6 bg-gradient-to-r from-[var(--brand-sand-100)]/50 to-white" data-testid="social-proof-section">
          <CardContent className="p-6 sm:p-8 text-center">
            <Clock className="h-8 w-8 text-[var(--brand-ocean-700)] mx-auto mb-3" />
            <p className="text-sm sm:text-base font-medium text-[var(--brand-slate-900)] mb-1" data-testid="social-proof-statement">
              Most quotes are delivered within 24–48 hours
            </p>
            <p className="text-xs sm:text-sm text-[var(--brand-slate-600)]" data-testid="social-proof-example">
              Recent project: Full home replacement in Kendall completed in 8 weeks
            </p>
            <p className="text-xs sm:text-sm text-[var(--brand-slate-600)] mt-2 italic" data-testid="authority-signal">
              Serving homeowners across Miami-Dade, Broward, and Palm Beach
            </p>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        {/* SECTION 6 – PRIMARY CTA (CONVERSION) */}
        <div className="mb-6" data-testid="primary-cta-section">
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-[var(--brand-slate-900)] mb-4 text-center">
            While You Wait...
          </h3>
          <Button
            onClick={() => navigate('/case-study-kendall-full-house')}
            size="lg"
            className="w-full bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-900)] text-white rounded-xl h-14 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            data-testid="primary-cta-case-studies"
          >
            <FileText className="mr-2 h-5 w-5" />
            See a Real Project Like Yours
          </Button>
          <p className="text-xs sm:text-sm text-[var(--brand-slate-600)] text-center mt-3">
            View a complete Kendall project breakdown with pricing, timeline, and results
          </p>
        </div>

        {/* SECTION 7 – MICRO-COMMITMENT CTA */}
        <Card className="rounded-2xl border-2 border-[var(--brand-coral-500)]/30 bg-gradient-to-br from-orange-50/50 via-white to-[var(--brand-coral-500)]/5 mb-6" data-testid="micro-commitment-section">
          <CardContent className="p-6 sm:p-8 text-center">
            <p className="font-heading text-base sm:text-lg font-semibold text-[var(--brand-slate-900)] mb-4" data-testid="micro-commitment-headline">
              Want your quote faster? Text us now.
            </p>
            <a
              href="tel:855-784-7627"
              className="inline-block w-full sm:w-auto"
              data-testid="micro-commitment-cta-text-us"
            >
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-[var(--brand-coral-500)] text-[var(--brand-coral-500)] hover:bg-[var(--brand-coral-500)] hover:text-white rounded-xl h-12 px-8 text-base font-semibold transition-all"
              >
                <Phone className="mr-2 h-5 w-5" />
                Text Us Now
              </Button>
            </a>
            <p className="text-xs sm:text-sm text-[var(--brand-slate-600)] mt-3">
              Call or text: <a href="tel:855-784-7627" className="font-mono font-semibold text-[var(--brand-ocean-700)] hover:underline">855-784-SNAP</a>
            </p>
          </CardContent>
        </Card>

        {/* SECTION 8 – SECONDARY LINKS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="secondary-links-section">
          <Button
            onClick={() => navigate('/impact-window-cost-miami')}
            variant="outline"
            className="h-12 rounded-xl border-2 text-[var(--brand-slate-900)] hover:bg-[hsl(var(--secondary))] transition-colors"
            data-testid="secondary-link-pricing"
          >
            Learn About Pricing
          </Button>
          <Button
            onClick={() => navigate('/impact-window-permits-miami-dade')}
            variant="outline"
            className="h-12 rounded-xl border-2 text-[var(--brand-slate-900)] hover:bg-[hsl(var(--secondary))] transition-colors"
            data-testid="secondary-link-process"
          >
            Explore Our Process
          </Button>
        </div>

        {/* FOOTER */}
        <div className="bg-[hsl(var(--secondary))]/50 rounded-xl p-4 text-center" data-testid="status-page-footer">
          <p className="text-xs text-[var(--brand-slate-600)] font-medium">
            Fully Licensed & Insured
          </p>
          <p className="text-xs text-[var(--brand-slate-600)] font-mono mt-1">
            License # CGC1524851
          </p>
        </div>
      </div>
    </div>
  );
}
